/**
 * Add Security Interest Panel — slide-over form for manually adding a new
 * security interest, or for converting an existing liability into one.
 *
 * When `convertFromLiabilityId` is set, the submit handler calls
 * `convertLiabilityToSecurityInterest` (which inserts the new SI and
 * soft-deletes the source liability) instead of `addManualSecurityInterest`.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { DatePicker } from "@/ui/composed/inputs/date-picker";

const PARTY_TYPE_OPTIONS = [
  { value: "third_party", label: "Third party" },
  { value: "related_party", label: "Related party" },
] as const;

const REGISTRATION_OPTIONS = [
  { value: "registered", label: "Registered" },
  { value: "unperfected", label: "Unperfected" },
] as const;

const COLLATERAL_TYPE_OPTIONS = [
  { value: "commercial_property", label: "Commercial property" },
  { value: "consumer_property", label: "Consumer property" },
] as const;

const COLLATERAL_CLASS_OPTIONS = [
  { value: "motor_vehicle", label: "Motor vehicle" },
  { value: "allpap_no_exceptions", label: "ALLPAP (no exceptions)" },
  { value: "allpap_with_exceptions", label: "ALLPAP (with exceptions)" },
  { value: "other_goods", label: "Other goods" },
  { value: "account", label: "Account" },
  { value: "intangible_property", label: "Intangible property" },
  { value: "financial_property", label: "Financial property" },
  { value: "chattel_paper", label: "Chattel paper" },
  { value: "general_intangible", label: "General intangible" },
  { value: "inventory", label: "Inventory" },
  { value: "negotiable_instrument", label: "Negotiable instrument" },
] as const;

const FIXED_FLOATING_OPTIONS = [
  { value: "fixed", label: "Fixed" },
  { value: "floating", label: "Floating" },
] as const;

export interface AddSecurityInterestPrefill {
  securedPartyName?: string;
  collateralDescription?: string;
  owedAmount?: number;
  owedAmountAsAt?: string;
  notes?: string;
}

interface AddSecurityInterestPanelProps {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  /** When set, submit converts this liability into the new SI (atomic). */
  convertFromLiabilityId?: Id<"liabilityEntries">;
  /** Pre-fill values from a converting liability (or any other source). */
  prefill?: AddSecurityInterestPrefill;
}

export function AddSecurityInterestPanel({
  jobId,
  taskId,
  open,
  onClose,
  onCreated,
  convertFromLiabilityId,
  prefill,
}: AddSecurityInterestPanelProps) {
  const [securedPartyName, setSecuredPartyName] = useState("");
  const [partyType, setPartyType] = useState<string>("third_party");
  const [registrationStatus, setRegistrationStatus] =
    useState<string>("unperfected");
  const [collateralType, setCollateralType] = useState<string>(
    "commercial_property",
  );
  const [collateralClass, setCollateralClass] = useState<string>("other_goods");
  const [collateralDescription, setCollateralDescription] = useState("");
  const [fixedOrFloating, setFixedOrFloating] = useState<string>("fixed");
  const [proceeds, setProceeds] = useState(false);
  const [inventory, setInventory] = useState(false);
  const [pmsi, setPmsi] = useState(false);
  const [owedAmount, setOwedAmount] = useState("");
  const [owedAmountAsAt, setOwedAmountAsAt] = useState("");
  const [notes, setNotes] = useState("");

  const addSI = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .addManualSecurityInterest,
    ),
  });
  const convertLiab = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .convertLiabilityToSecurityInterest,
    ),
  });

  const isConverting = convertFromLiabilityId != null;
  const isPending = isConverting ? convertLiab.isPending : addSI.isPending;

  function resetForm() {
    setSecuredPartyName("");
    setPartyType("third_party");
    setRegistrationStatus("unperfected");
    setCollateralType("commercial_property");
    setCollateralClass("other_goods");
    setCollateralDescription("");
    setFixedOrFloating("fixed");
    setProceeds(false);
    setInventory(false);
    setPmsi(false);
    setOwedAmount("");
    setOwedAmountAsAt("");
    setNotes("");
  }

  // Hydrate from prefill whenever the sheet opens (false -> true). We
  // intentionally key off `open` only so subsequent parent re-renders (which
  // create new `prefill` object references) don't clobber the user's mid-form
  // edits. The latest `prefill` value is read via the ref captured below.
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;
  useEffect(() => {
    if (!open) return;
    const p = prefillRef.current;
    setSecuredPartyName(p?.securedPartyName ?? "");
    setCollateralDescription(p?.collateralDescription ?? "");
    setOwedAmount(p?.owedAmount != null ? String(p.owedAmount) : "");
    setOwedAmountAsAt(p?.owedAmountAsAt ?? "");
    setNotes(p?.notes ?? "");
  }, [open]);

  async function handleSubmit() {
    if (!securedPartyName.trim() || !collateralDescription.trim()) return;

    const owedNum = owedAmount.trim()
      ? Number(owedAmount.replace(/[$,\s]/g, ""))
      : undefined;
    const cleanOwed =
      owedNum != null && Number.isFinite(owedNum) && owedNum >= 0
        ? owedNum
        : undefined;

    const sharedArgs = {
      securedPartyName: securedPartyName.trim(),
      partyType: partyType as "third_party" | "related_party",
      registrationStatus: registrationStatus as "registered" | "unperfected",
      collateralType: collateralType as
        "commercial_property" | "consumer_property",
      collateralClass: collateralClass as
        | "motor_vehicle"
        | "allpap_no_exceptions"
        | "allpap_with_exceptions"
        | "other_goods"
        | "account"
        | "intangible_property"
        | "financial_property"
        | "chattel_paper"
        | "general_intangible"
        | "inventory"
        | "negotiable_instrument",
      collateralDescription: collateralDescription.trim(),
      fixedOrFloating: fixedOrFloating as "fixed" | "floating",
      proceeds,
      inventory,
      pmsi,
      owedAmount: cleanOwed,
      owedAmountAsAt: owedAmountAsAt || undefined,
      notes: notes.trim() || undefined,
    };

    const result = isConverting
      ? await convertLiab.mutateAsync({
          entryId: convertFromLiabilityId,
          ...sharedArgs,
        })
      : await addSI.mutateAsync({ jobId, taskId, ...sharedArgs });

    resetForm();
    onCreated(result as unknown as string);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          resetForm();
          onClose();
        }
      }}
    >
      <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-md">
        <div className="px-6 pb-4 pt-6">
          <SheetHeader>
            <SheetTitle className="text-lg font-bold">
              {isConverting
                ? "Convert to Security Interest"
                : "Add Security Interest"}
            </SheetTitle>
            <SheetDescription>
              {isConverting
                ? "Confirm the security interest details. The original liability will be removed on save."
                : "Manually add a security interest to the summary."}
            </SheetDescription>
          </SheetHeader>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 px-6 pb-6">
            {/* Secured Party Name */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Secured Party Name *
              </label>
              <Input
                value={securedPartyName}
                onChange={(e) => setSecuredPartyName(e.target.value)}
                placeholder="Name of secured party"
                className="h-9 text-sm"
              />
            </div>

            {/* Party Type */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Party Type
              </label>
              <Select value={partyType} onValueChange={setPartyType}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARTY_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Registration Status */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Registration Status
              </label>
              <Select
                value={registrationStatus}
                onValueChange={setRegistrationStatus}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REGISTRATION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Collateral Type */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Collateral Type
              </label>
              <Select value={collateralType} onValueChange={setCollateralType}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLLATERAL_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Collateral Class */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Collateral Class
              </label>
              <Select
                value={collateralClass}
                onValueChange={setCollateralClass}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLLATERAL_CLASS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Collateral Description */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Collateral Description *
              </label>
              <Textarea
                value={collateralDescription}
                onChange={(e) => setCollateralDescription(e.target.value)}
                placeholder="Describe the collateral (e.g. motor vehicle, VIN, plant equipment...)"
                rows={3}
                className="text-sm"
              />
            </div>

            {/* Fixed / Floating */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Fixed / Floating
              </label>
              <Select
                value={fixedOrFloating}
                onValueChange={setFixedOrFloating}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIXED_FLOATING_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Proceeds / Inventory / PMSI */}
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ["Proceeds", proceeds, setProceeds],
                  ["Inventory", inventory, setInventory],
                  ["PMSI", pmsi, setPmsi],
                ] as const
              ).map(([label, value, setter]) => (
                <div key={label}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {label}
                  </label>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant={value ? "outline" : "secondary"}
                      size="sm"
                      className="h-8 flex-1 text-xs"
                      onClick={() => setter(false)}
                    >
                      No
                    </Button>
                    <Button
                      type="button"
                      variant={value ? "secondary" : "outline"}
                      size="sm"
                      className="h-8 flex-1 text-xs"
                      onClick={() => setter(true)}
                    >
                      Yes
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Owed Amount */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Owed Amount
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  $
                </span>
                <Input
                  inputMode="decimal"
                  value={owedAmount}
                  onChange={(e) => setOwedAmount(e.target.value)}
                  placeholder="0.00"
                  className="h-9 pl-5 text-sm tabular-nums"
                />
              </div>
            </div>

            {/* As at Date */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                As at Date (optional)
              </label>
              <DatePicker value={owedAmountAsAt} onChange={setOwedAmountAsAt} />
            </div>

            {/* Notes */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notes
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any relevant details..."
                rows={3}
                className="text-sm"
              />
            </div>

            {/* Submit */}
            <Button
              className="w-full"
              disabled={
                !securedPartyName.trim() ||
                !collateralDescription.trim() ||
                isPending
              }
              onClick={handleSubmit}
            >
              {isPending
                ? isConverting
                  ? "Converting..."
                  : "Adding..."
                : isConverting
                  ? "Convert"
                  : "Add Security Interest"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
